import { useEffect, useState } from 'react';

/**
 * What this browser/device can do for a WebAuthn passwordless credential.
 *
 * - `checking`     — the platform-authenticator probe is still in flight (briefly, on every mount).
 * - `unsupported`  — no WebAuthn at all (`PublicKeyCredential` is undefined); password-free sign-in is
 *   simply not possible here, on any device.
 * - `platform`     — a user-verifying platform authenticator is available (Face ID, Touch ID, Windows
 *   Hello, Android biometrics) — the common case on a personal laptop or phone.
 * - `cross-device` — WebAuthn exists but no built-in biometric was found (or the probe itself failed);
 *   still usable through a phone (QR code / hybrid transport) or a hardware security key.
 */
export type WebAuthnCapability = 'checking' | 'unsupported' | 'platform' | 'cross-device';

/**
 * Synchronous presence check — just "does the WebAuthn API exist at all", with none of the async
 * platform-authenticator probing {@link useWebAuthnCapability} does. This is all the post-login nudge
 * needs (it must not offer password-free sign-in on a browser that cannot do it at all), and it needs
 * it without the hook's transient `checking` state: the nudge's trigger is evaluated on every render,
 * not staged through a loading state.
 */
export function hasWebAuthnSupport(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';
}

/**
 * Detects what this browser can do for the passkey card (user-story-merchant-portal-060 AC5).
 *
 * Two feature checks, from the most to the least capable outcome:
 *
 * 1. `window.PublicKeyCredential` — its mere presence is the WebAuthn API itself. Absent on an
 *    outdated or locked-down corporate browser; there the card must hide its primary CTA entirely
 *    rather than offer a button that can only fail.
 * 2. `PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()` — whether *this device* has
 *    a built-in biometric/PIN authenticator. A `false` resolution or a rejected promise are treated the
 *    same way (`cross-device`): either means "no built-in option", and a hardware key or a phone is
 *    still available either way, so a probe failure must not be read as "unsupported".
 */
export function useWebAuthnCapability(): WebAuthnCapability {
  const [capability, setCapability] = useState<WebAuthnCapability>('checking');

  useEffect(() => {
    let cancelled = false;

    if (!hasWebAuthnSupport()) {
      setCapability('unsupported');
      return undefined;
    }
    const publicKeyCredential = window.PublicKeyCredential;

    const probe = publicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable;
    if (typeof probe !== 'function') {
      // The API exists but this particular capability check does not — treat like any other
      // undetermined platform authenticator: a hardware key / phone is still on offer.
      setCapability('cross-device');
      return undefined;
    }

    probe
      .call(publicKeyCredential)
      .then((available) => {
        if (!cancelled) {
          setCapability(available ? 'platform' : 'cross-device');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCapability('cross-device');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return capability;
}
